import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import Toast, { ToastType } from '../components/Toast';
import ConfirmDialog from '../components/ConfirmDialog';
import HtmlEditor from '../components/HtmlEditor';
import { apiService } from '../services/api';
import { FiSettings, FiFileText, FiImage, FiMessageSquare, FiSave, FiX, FiPlus, FiHelpCircle } from 'react-icons/fi';

// File type options for CV uploads
const CV_FILE_TYPE_OPTIONS = [
  { value: '.pdf', label: 'PDF (.pdf)', category: 'Document' },
  { value: '.doc', label: 'Word 97-2003 (.doc)', category: 'Document' },
  { value: '.docx', label: 'Word (.docx)', category: 'Document' },
  { value: '.rtf', label: 'Rich Text (.rtf)', category: 'Document' },
  { value: '.txt', label: 'Plain Text (.txt)', category: 'Document' },
  { value: '.odt', label: 'OpenDocument (.odt)', category: 'Document' },
];

// File type options for Logo/Image uploads
const IMAGE_FILE_TYPE_OPTIONS = [
  { value: '.jpg', label: 'JPEG (.jpg)', category: 'Image' },
  { value: '.jpeg', label: 'JPEG (.jpeg)', category: 'Image' },
  { value: '.png', label: 'PNG (.png)', category: 'Image' },
  { value: '.gif', label: 'GIF (.gif)', category: 'Image' },
  { value: '.webp', label: 'WebP (.webp)', category: 'Image' },
  { value: '.svg', label: 'SVG (.svg)', category: 'Image' },
  { value: '.bmp', label: 'Bitmap (.bmp)', category: 'Image' },
  { value: '.ico', label: 'Icon (.ico)', category: 'Image' },
];

interface AppSettingsData {
  id: number;
  app_name: string;
  app_name_fr: string;
  app_description: string;
  app_description_fr: string;
  cv_allowed_types: string;
  cv_max_size_kb: number;
  logo_allowed_types: string;
  logo_max_size_kb: number;
  default_language: string;
  enable_english: boolean;
  enable_french: boolean;
  enable_language_switcher: boolean;
  news_title_en: string;
  news_title_fr: string;
  news_content_en: string;
  news_content_fr: string;
  news_enabled: boolean;
  package_title_en: string;
  package_title_fr: string;
  package_description_en: string;
  package_description_fr: string;
  packages_textarea_en: string;
  packages_textarea_fr: string;
  footer_text_en: string;
  footer_text_fr: string;
  // Simplified Legal Links - just URLs
  terms_link_en: string;
  terms_link_fr: string;
  privacy_link_en: string;
  privacy_link_fr: string;
  // Default Language Weight
  default_language_percent: number;
  // Contact Information (for billing)
  contact_title: string;
  contact_address: string;
  contact_zip: string;
  contact_city: string;
  contact_country: string;
  contact_vat: string;
  contact_vat_percent: number;
  contact_invoice_footer_en: string;
  contact_invoice_footer_fr: string;
  support_content_en: string;
  support_content_fr: string;
  carousel_images?: Array<{ id: string; url: string; alt_text: string; order: number }>;
  carousel_slide_delay?: number;
  updated_at: string;
}

const AppSettings: React.FC = () => {
  const [settings, setSettings] = useState<AppSettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [activeSection, setActiveSection] = useState<string>('app');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [carouselImages, setCarouselImages] = useState<Array<{ id: string; url: string; alt_text: string; order: number }>>([]);
  const [uploadingCarousel, setUploadingCarousel] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'danger' | 'warning' | 'success';
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'danger',
    onConfirm: () => {},
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await apiService.getAppSettings();
      if (response.success && response.data) {
        setSettings(response.data);
        if (response.data.carousel_images) {
          setCarouselImages(response.data.carousel_images);
        }
      }
      
      // Also fetch carousel images separately
      const carouselResponse = await apiService.getCarouselImages();
      if (carouselResponse.success && carouselResponse.data?.carousel_images) {
        setCarouselImages(carouselResponse.data.carousel_images);
      }
    } catch (error) {
      setToast({ message: 'Failed to load app settings', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!settings) return;

    setSaving(true);
    setFieldErrors({}); // Clear previous errors

    try {
      const response = await apiService.updateAppSettings(settings);
      if (response.success) {
        setToast({ message: 'Settings saved successfully', type: 'success' });
        // Update settings with response data if available
        if (response.data) {
          setSettings(response.data);
        }
      } else {
        // Handle validation errors - cast response to access error properties
        const errorResponse = response as any;
        if (errorResponse.error && errorResponse.error.detail) {
          const errors: Record<string, string[]> = {};

          // Parse field-specific errors
          Object.keys(errorResponse.error.detail).forEach(field => {
            const fieldMessages = errorResponse.error.detail[field];
            if (Array.isArray(fieldMessages)) {
              errors[field] = fieldMessages;
            } else if (typeof fieldMessages === 'string') {
              errors[field] = [fieldMessages];
            }
          });

          setFieldErrors(errors);

          // Show general error message
          const errorCount = Object.keys(errors).length;
          setToast({
            message: `Please fix ${errorCount} validation error${errorCount > 1 ? 's' : ''} below`,
            type: 'error'
          });
        } else {
          // Fallback for non-validation errors
          setToast({ message: errorResponse.error?.message || 'Failed to save settings', type: 'error' });
        }
      }
    } catch (error: any) {
      // Handle network or unexpected errors
      if (error.response?.data?.error?.detail) {
        const errors: Record<string, string[]> = {};

        Object.keys(error.response.data.error.detail).forEach(field => {
          const fieldMessages = error.response.data.error.detail[field];
          if (Array.isArray(fieldMessages)) {
            errors[field] = fieldMessages;
          } else if (typeof fieldMessages === 'string') {
            errors[field] = [fieldMessages];
          }
        });

        setFieldErrors(errors);

        const errorCount = Object.keys(errors).length;
        setToast({
          message: `Please fix ${errorCount} validation error${errorCount > 1 ? 's' : ''} below`,
          type: 'error'
        });
      } else {
        setToast({ message: error.message || 'Failed to save settings', type: 'error' });
      }
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field: keyof AppSettingsData, value: any) => {
    if (settings) {
      setSettings({ ...settings, [field]: value });
      // Clear field error when user starts typing
      if (fieldErrors[field]) {
        setFieldErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    }
  };

  // Carousel management functions
  const handleCarouselUpload = async (file: File) => {
    setUploadingCarousel(true);
    try {
      const response = await apiService.uploadCarouselImage(file, '');
      if (response.success && response.data) {
        const newImage = response.data;
        setCarouselImages([...carouselImages, newImage]);
        setToast({ message: 'Image uploaded successfully', type: 'success' });
      }
    } catch (error) {
      setToast({ message: 'Failed to upload image', type: 'error' });
    } finally {
      setUploadingCarousel(false);
    }
  };

  const handleCarouselDelete = (imageId: string) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Carousel Image',
      message: 'Are you sure you want to delete this image? This action cannot be undone.',
      type: 'danger',
      onConfirm: () => performCarouselDelete(imageId),
    });
  };

  const performCarouselDelete = async (imageId: string) => {
    try {
      const response = await apiService.deleteCarouselImage(imageId);
      if (response.success) {
        setCarouselImages(carouselImages.filter(img => img.id !== imageId));
        setToast({ message: 'Image deleted successfully', type: 'success' });
      }
    } catch (error) {
      setToast({ message: 'Failed to delete image', type: 'error' });
    }
  };

  const handleCarouselReorder = async (fromIndex: number, toIndex: number) => {
    console.log('🔄 Reordering images:', { fromIndex, toIndex });
    console.log('📋 Current images:', carouselImages.map((img, idx) => ({ idx, id: img.id, order: img.order })));
    
    const newImages = [...carouselImages];
    const [removed] = newImages.splice(fromIndex, 1);
    newImages.splice(toIndex, 0, removed);
    
    // Update order field for each image
    const reorderedImages = newImages.map((img, index) => ({
      ...img,
      order: index
    }));
    
    console.log('🎯 New order:', reorderedImages.map((img, idx) => ({ idx, id: img.id, order: img.order })));
    
    // Optimistically update UI
    setCarouselImages(reorderedImages);
    
    try {
      const imageIds = reorderedImages.map(img => img.id);
      console.log('📤 Sending reorder request with IDs:', imageIds);
      
      const response = await apiService.reorderCarouselImages(imageIds);
      console.log('📥 Reorder response:', response);
      
      if (response.success) {
        setToast({ message: 'Images reordered successfully', type: 'success' });
        // Update with server response if available
        if (response.data?.carousel_images) {
          console.log('✅ Updated with server data:', response.data.carousel_images);
          setCarouselImages(response.data.carousel_images);
        }
      } else {
        console.error('❌ Reorder failed:', response.error);
        // Revert on failure
        setCarouselImages(carouselImages);
        setToast({ message: response.error?.message || 'Failed to reorder images', type: 'error' });
      }
    } catch (error: any) {
      console.error('💥 Reorder error:', error);
      // Revert on error
      setCarouselImages(carouselImages);
      setToast({ message: error.message || 'Failed to reorder images', type: 'error' });
    }
  };

  const sections = [
    { id: 'app', label: 'App Info', icon: FiSettings },
    { id: 'cv', label: 'CV Upload', icon: FiFileText },
    { id: 'logo', label: 'Logo Upload', icon: FiImage },
    { id: 'content', label: 'Content', icon: FiMessageSquare },
    { id: 'support', label: 'Support', icon: FiHelpCircle },
    { id: 'carousel', label: 'Auth Carousel', icon: FiImage },
  ];

  // Helper component to display field errors
  const FieldError: React.FC<{ fieldName: string }> = ({ fieldName }) => {
    const errors = fieldErrors[fieldName];
    if (!errors || errors.length === 0) return null;

    return (
      <div className="mt-1 space-y-1">
        {errors.map((error, index) => (
          <p key={index} className="flex items-start gap-1 text-sm text-red-600 dark:text-red-400">
            <span className="text-red-500">⚠</span>
            <span>{error}</span>
          </p>
        ))}
      </div>
    );
  };

  // Helper function to get error styling for input fields
  const getFieldClassName = (fieldName: string, baseClassName: string) => {
    const hasError = fieldErrors[fieldName] && fieldErrors[fieldName].length > 0;
    return hasError
      ? `${baseClassName} border-red-300 focus:border-red-500 focus:ring-red-500 dark:border-red-600`
      : baseClassName;
  };

  // File Type Selector Component
  const FileTypeSelector: React.FC<{
    selectedTypes: string;
    onChange: (types: string) => void;
    options: typeof CV_FILE_TYPE_OPTIONS;
    color: 'green' | 'purple';
  }> = ({ selectedTypes, onChange, options, color }) => {
    const selectedArray = selectedTypes.split(',').map(t => t.trim()).filter(t => t);
    const [isOpen, setIsOpen] = useState(false);

    const toggleType = (type: string) => {
      if (selectedArray.includes(type)) {
        const newTypes = selectedArray.filter(t => t !== type);
        onChange(newTypes.join(', '));
      } else {
        const newTypes = [...selectedArray, type];
        onChange(newTypes.join(', '));
      }
    };

    const removeType = (type: string) => {
      const newTypes = selectedArray.filter(t => t !== type);
      onChange(newTypes.join(', '));
    };

    const colorClasses = {
      green: {
        tag: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
        tagHover: 'hover:bg-green-200 dark:hover:bg-green-900/50',
        button: 'text-green-600 hover:bg-green-100 dark:text-green-400 dark:hover:bg-green-900/30',
        check: 'bg-green-500',
      },
      purple: {
        tag: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
        tagHover: 'hover:bg-purple-200 dark:hover:bg-purple-900/50',
        button: 'text-purple-600 hover:bg-purple-100 dark:text-purple-400 dark:hover:bg-purple-900/30',
        check: 'bg-purple-500',
      },
    };

    const colors = colorClasses[color];

    return (
      <div className="space-y-3">
        {/* Selected Tags */}
        <div className="flex flex-wrap gap-2 min-h-[40px] rounded-lg border border-gray-300 p-2 dark:border-gray-600 dark:bg-navy-700">
          {selectedArray.length === 0 ? (
            <span className="text-sm text-gray-400 dark:text-gray-500 py-1">No file types selected</span>
          ) : (
            selectedArray.map((type) => (
              <span
                key={type}
                className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-medium ${colors.tag}`}
              >
                {type}
                <button
                  type="button"
                  onClick={() => removeType(type)}
                  className={`ml-1 rounded-full p-0.5 ${colors.tagHover}`}
                >
                  <FiX className="h-3 w-3" />
                </button>
              </span>
            ))
          )}
        </div>

        {/* Add Button */}
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition ${colors.button}`}
        >
          <FiPlus className="h-4 w-4" />
          {isOpen ? 'Hide Options' : 'Add File Types'}
        </button>

        {/* Options Grid */}
        {isOpen && (
          <div className="grid grid-cols-2 gap-2 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
            {options.map((option) => {
              const isSelected = selectedArray.includes(option.value);
              return (
                <label
                  key={option.value}
                  className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition ${isSelected
                    ? `border-${color}-500 bg-${color}-50 dark:bg-${color}-900/20`
                    : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
                    }`}
                  onClick={() => toggleType(option.value)}
                >
                  <div
                    className={`flex h-5 w-5 items-center justify-center rounded border-2 transition ${isSelected
                      ? `${colors.check} border-transparent`
                      : 'border-gray-300 dark:border-gray-600'
                      }`}
                  >
                    {isSelected && (
                      <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 12 12">
                        <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{option.label}</span>
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const Toggle: React.FC<{ enabled: boolean; onChange: (val: boolean) => void; label: string }> = ({ enabled, onChange, label }) => (
    <label className="flex cursor-pointer items-center justify-between rounded-lg border border-gray-200 p-4 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-navy-700">
      <span className="font-medium text-gray-700 dark:text-gray-300">{label}</span>
      <button
        type="button"
        onClick={() => onChange(!enabled)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 ${enabled ? 'bg-brand-500' : 'bg-gray-300 dark:bg-gray-600'
          }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${enabled ? 'translate-x-5' : 'translate-x-0'
            }`}
        />
      </button>
    </label>
  );


  if (loading) {
    return (
      <Layout>
        <div className="flex h-64 items-center justify-center">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-brand-500 border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  if (!settings) {
    return (
      <Layout>
        <div className="flex h-64 items-center justify-center">
          <p className="text-gray-500 dark:text-gray-400">Failed to load settings</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">App Settings</h1>

          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 rounded-lg bg-brand-500 px-6 py-2.5 font-medium text-white hover:bg-brand-600 disabled:opacity-50"
          >
            <FiSave className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        {/* Navigation Tabs */}
        <div className="flex flex-wrap gap-2 rounded-xl bg-white p-2 shadow dark:bg-navy-800">
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition ${activeSection === section.id
                ? 'bg-brand-500 text-white'
                : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-navy-700'
                }`}
            >
              <section.icon className="h-4 w-4" />
              {section.label}
            </button>
          ))}
        </div>

        {/* Content Sections */}
        <div className="rounded-xl bg-white p-6 shadow-lg dark:bg-navy-800">
          {/* App Info Section */}
          {activeSection === 'app' && (
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    App Name (English)
                  </label>
                  <input
                    type="text"
                    value={settings.app_name}
                    onChange={(e) => updateField('app_name', e.target.value)}
                    className={getFieldClassName('app_name', "w-full rounded-lg border border-gray-300 px-4 py-3 dark:border-gray-600 dark:bg-navy-700 dark:text-white")}
                  />
                  <FieldError fieldName="app_name" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    App Name (French)
                  </label>
                  <input
                    type="text"
                    value={settings.app_name_fr || ''}
                    onChange={(e) => updateField('app_name_fr', e.target.value)}
                    className={getFieldClassName('app_name_fr', "w-full rounded-lg border border-gray-300 px-4 py-3 dark:border-gray-600 dark:bg-navy-700 dark:text-white")}
                  />
                  <FieldError fieldName="app_name_fr" />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    App Description (English)
                  </label>
                  <textarea
                    value={settings.app_description}
                    onChange={(e) => updateField('app_description', e.target.value)}
                    rows={3}
                    className={getFieldClassName('app_description', "w-full rounded-lg border border-gray-300 px-4 py-3 dark:border-gray-600 dark:bg-navy-700 dark:text-white")}
                  />
                  <FieldError fieldName="app_description" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    App Description (French)
                  </label>
                  <textarea
                    value={settings.app_description_fr || ''}
                    onChange={(e) => updateField('app_description_fr', e.target.value)}
                    rows={3}
                    className={getFieldClassName('app_description_fr', "w-full rounded-lg border border-gray-300 px-4 py-3 dark:border-gray-600 dark:bg-navy-700 dark:text-white")}
                  />
                  <FieldError fieldName="app_description_fr" />
                </div>
              </div>

              {/* Package Page Meta Fields */}
              <div className="mt-6 border-t border-gray-200 pt-6 dark:border-gray-700">
                <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                  Package Page Meta Tags
                </h3>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Package Page Title (English)
                    </label>
                    <input
                      type="text"
                      value={settings.package_title_en || ''}
                      onChange={(e) => updateField('package_title_en', e.target.value)}
                      placeholder="Choose Your Perfect Plan"
                      className={getFieldClassName('package_title_en', "w-full rounded-lg border border-gray-300 px-4 py-3 dark:border-gray-600 dark:bg-navy-700 dark:text-white")}
                    />
                    <FieldError fieldName="package_title_en" />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Package Page Title (French)
                    </label>
                    <input
                      type="text"
                      value={settings.package_title_fr || ''}
                      onChange={(e) => updateField('package_title_fr', e.target.value)}
                      placeholder="Choisissez Votre Plan Parfait"
                      className={getFieldClassName('package_title_fr', "w-full rounded-lg border border-gray-300 px-4 py-3 dark:border-gray-600 dark:bg-navy-700 dark:text-white")}
                    />
                    <FieldError fieldName="package_title_fr" />
                  </div>
                </div>

                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Package Page Description (English)
                    </label>
                    <textarea
                      value={settings.package_description_en || ''}
                      onChange={(e) => updateField('package_description_en', e.target.value)}
                      rows={3}
                      placeholder="Select the package that best fits your recruitment needs"
                      className={getFieldClassName('package_description_en', "w-full rounded-lg border border-gray-300 px-4 py-3 dark:border-gray-600 dark:bg-navy-700 dark:text-white")}
                    />
                    <FieldError fieldName="package_description_en" />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Package Page Description (French)
                    </label>
                    <textarea
                      value={settings.package_description_fr || ''}
                      onChange={(e) => updateField('package_description_fr', e.target.value)}
                      rows={3}
                      placeholder="Sélectionnez le forfait qui correspond le mieux à vos besoins de recrutement"
                      className={getFieldClassName('package_description_fr', "w-full rounded-lg border border-gray-300 px-4 py-3 dark:border-gray-600 dark:bg-navy-700 dark:text-white")}
                    />
                    <FieldError fieldName="package_description_fr" />
                  </div>
                </div>

                {/* Package Page Additional Content (HTML Editor) */}
                <div className="mt-4">
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Additional Content (English)
                  </label>
                  <HtmlEditor
                    value={settings.packages_textarea_en || ''}
                    onChange={(html) => updateField('packages_textarea_en', html)}
                    placeholder="Enter additional content to display below title and description..."
                    minHeight="250px"
                    showPlaceholders={false}
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Content displayed below title and description on packages page. Leave empty to hide.
                  </p>
                  <FieldError fieldName="packages_textarea_en" />
                </div>

                <div className="mt-4">
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Additional Content (French)
                  </label>
                  <HtmlEditor
                    value={settings.packages_textarea_fr || ''}
                    onChange={(html) => updateField('packages_textarea_fr', html)}
                    placeholder="Entrez du contenu supplémentaire à afficher sous le titre et la description..."
                    minHeight="250px"
                    showPlaceholders={false}
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Contenu affiché sous le titre et la description sur la page des forfaits. Laisser vide pour masquer.
                  </p>
                  <FieldError fieldName="packages_textarea_fr" />
                </div>
              </div>
            </div>
          )}

          {/* CV Upload Section */}
          {activeSection === 'cv' && (
            <div className="space-y-6">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Allowed File Types
                </label>
                <FileTypeSelector
                  selectedTypes={settings.cv_allowed_types}
                  onChange={(types) => updateField('cv_allowed_types', types)}
                  options={CV_FILE_TYPE_OPTIONS}
                  color="green"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Maximum File Size (KB)
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={settings.cv_max_size_kb}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '' || /^\d+$/.test(v)) {
                        updateField('cv_max_size_kb', v === '' ? 0 : parseInt(v));
                      }
                    }}
                    className="w-full max-w-xs rounded-lg border border-gray-300 px-4 py-3 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                  />
                  <span className="whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    = {(settings.cv_max_size_kb / 1024).toFixed(1)} MB
                  </span>
                </div>
              </div>

            </div>
          )}

          {/* Logo Upload Section */}
          {activeSection === 'logo' && (
            <div className="space-y-6">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Allowed File Types
                </label>
                <FileTypeSelector
                  selectedTypes={settings.logo_allowed_types}
                  onChange={(types) => updateField('logo_allowed_types', types)}
                  options={IMAGE_FILE_TYPE_OPTIONS}
                  color="purple"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Maximum File Size (KB)
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={settings.logo_max_size_kb}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '' || /^\d+$/.test(v)) {
                        updateField('logo_max_size_kb', v === '' ? 0 : parseInt(v));
                      }
                    }}
                    className="w-full max-w-xs rounded-lg border border-gray-300 px-4 py-3 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                  />
                  <span className="whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    = {(settings.logo_max_size_kb / 1024).toFixed(1)} MB
                  </span>
                </div>
              </div>


            </div>
          )}

          {/* Content Section */}
          {activeSection === 'content' && (
            <div className="space-y-6">
              {/* News Section */}
              <div className="rounded-xl border border-gray-200 p-5 dark:border-gray-700">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-md font-semibold text-gray-900 dark:text-white">📰 News Section</h3>
                  <Toggle
                    enabled={settings.news_enabled}
                    onChange={(val) => updateField('news_enabled', val)}
                    label="Show News"
                  />
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Title (EN)</label>
                    <input
                      type="text"
                      value={settings.news_title_en}
                      onChange={(e) => updateField('news_title_en', e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Title (FR)</label>
                    <input
                      type="text"
                      value={settings.news_title_fr}
                      onChange={(e) => updateField('news_title_fr', e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Content (EN)</label>
                    <textarea
                      value={settings.news_content_en}
                      onChange={(e) => updateField('news_content_en', e.target.value)}
                      rows={3}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Content (FR)</label>
                    <textarea
                      value={settings.news_content_fr}
                      onChange={(e) => updateField('news_content_fr', e.target.value)}
                      rows={3}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Footer Section */}
              <div className="rounded-xl border border-gray-200 p-5 dark:border-gray-700">
                <h3 className="mb-4 text-md font-semibold text-gray-900 dark:text-white">📄 Footer Text</h3>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Footer (EN)</label>
                    <textarea
                      value={settings.footer_text_en}
                      onChange={(e) => updateField('footer_text_en', e.target.value)}
                      rows={2}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Footer (FR)</label>
                    <textarea
                      value={settings.footer_text_fr}
                      onChange={(e) => updateField('footer_text_fr', e.target.value)}
                      rows={2}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Legal Links Section */}
              <div className="rounded-xl border border-gray-200 p-5 dark:border-gray-700">
                <h3 className="mb-4 text-md font-semibold text-gray-900 dark:text-white">🔗 Legal Links</h3>

                {/* Terms & Conditions */}
                <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
                  <h4 className="mb-3 text-sm font-semibold text-blue-700 dark:text-blue-300">📜 Terms & Conditions</h4>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Terms URL (English)</label>
                      <input
                        type="url"
                        value={settings.terms_link_en || ''}
                        onChange={(e) => updateField('terms_link_en', e.target.value)}
                        placeholder="https://example.com/terms-en"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Terms URL (French)</label>
                      <input
                        type="url"
                        value={settings.terms_link_fr || ''}
                        onChange={(e) => updateField('terms_link_fr', e.target.value)}
                        placeholder="https://example.com/terms-fr"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                  </div>

                </div>

                {/* Privacy Policy */}
                <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
                  <h4 className="mb-3 text-sm font-semibold text-green-700 dark:text-green-300">🔒 Privacy Policy</h4>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Privacy URL (English)</label>
                      <input
                        type="url"
                        value={settings.privacy_link_en || ''}
                        onChange={(e) => updateField('privacy_link_en', e.target.value)}
                        placeholder="https://example.com/privacy-en"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Privacy URL (French)</label>
                      <input
                        type="url"
                        value={settings.privacy_link_fr || ''}
                        onChange={(e) => updateField('privacy_link_fr', e.target.value)}
                        placeholder="https://example.com/privacy-fr"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                  </div>

                </div>
              </div>
            </div>
          )}

          {/* Support Section */}
          {activeSection === 'support' && (
            <div className="space-y-6">
              <div className="rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
              
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Support Content (English)
                  </label>
                  <HtmlEditor
                    value={settings.support_content_en || ''}
                    onChange={(html) => updateField('support_content_en', html)}
                    placeholder="Enter support content in English..."
                  />
                  <FieldError fieldName="support_content_en" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Support Content (French)
                  </label>
                  <HtmlEditor
                    value={settings.support_content_fr || ''}
                    onChange={(html) => updateField('support_content_fr', html)}
                    placeholder="Entrez le contenu du support en français..."
                  />
                  <FieldError fieldName="support_content_fr" />
                </div>
              </div>
            </div>
          )}

          {/* Carousel Section */}
          {activeSection === 'carousel' && (
            <div className="space-y-6">
              {/* Slide timing */}
              <div className="max-w-xs">
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Sliding delay (seconds)
                </label>
                <input
                  type="number"
                  min={1}
                  value={settings.carousel_slide_delay ?? 10}
                  onChange={(e) => updateField('carousel_slide_delay', parseInt(e.target.value, 10) || 1)}
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Time each image is shown before sliding to the next. Saved with the "Save" button above.
                </p>
              </div>

              {/* Image Management */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Manage Images ({carouselImages.length})
                  </h4>
                  <label className="cursor-pointer inline-flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50">
                    <FiPlus className="h-4 w-4" />
                    {uploadingCarousel ? 'Uploading...' : 'Upload Image'}
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp,image/gif,image/svg+xml,.svg"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handleCarouselUpload(file);
                        }
                      }}
                      className="hidden"
                      disabled={uploadingCarousel}
                    />
                  </label>
                </div>

                {/* Image List */}
                {carouselImages.length === 0 ? (
                  <div className="rounded-lg border-2 border-dashed border-gray-300 p-8 text-center dark:border-gray-600">
                    <FiImage className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                      No carousel images uploaded yet
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                      Recommended size: 1200x800px (3:2 aspect ratio). JPG, PNG, WEBP, GIF or SVG.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {carouselImages
                      .sort((a, b) => a.order - b.order)
                      .map((image, index) => (
                        <div
                          key={image.id}
                          className="relative group rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
                        >
                          <div className="aspect-video bg-gray-100 dark:bg-gray-800">
                            <img
                              src={image.url}
                              alt={image.alt_text || 'Carousel image'}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                            {index > 0 && (
                              <button
                                type="button"
                                onClick={() => handleCarouselReorder(index, index - 1)}
                                className="p-2 bg-white/90 text-gray-800 rounded-lg hover:bg-white"
                                title="Move left"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleCarouselDelete(image.id)}
                              className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                              title="Delete"
                            >
                              <FiX className="w-4 h-4" />
                            </button>
                            {index < carouselImages.length - 1 && (
                              <button
                                type="button"
                                onClick={() => handleCarouselReorder(index, index + 1)}
                                className="p-2 bg-white/90 text-gray-800 rounded-lg hover:bg-white"
                                title="Move right"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </button>
                            )}
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-1 text-center">
                            #{index + 1}
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Last Updated */}
        {settings.updated_at && (
          <p className="text-center text-xs text-gray-400 dark:text-gray-500">
            Last updated: {new Date(settings.updated_at).toLocaleString()}
          </p>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </Layout>
  );
};

export default AppSettings;
